import { useId } from "react";
import { fishOrientationDegrees, fishPhysicsGeometry, marineTentaclePath, type FishPhysicsGeometry, type FishPhysicsRig, type MarineVec2 } from "./marinePhysics";

interface FishPhysicsCharacterProps {
  model: FishPhysicsRig;
  heading: number;
  className?: string;
}

function pointText(point: MarineVec2): string {
  return `${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
}

function quadraticPath(start: MarineVec2, control: MarineVec2, end: MarineVec2): string {
  return `M ${pointText(start)} Q ${pointText(control)} ${pointText(end)}`;
}

function finPath(fin: FishPhysicsGeometry["fins"][number]): string {
  const normal = { x: -Math.sin(fin.angle) * 9, y: Math.cos(fin.angle) * 9 };
  return `M ${pointText(fin.root)} Q ${pointText({ x: fin.mid.x + normal.x, y: fin.mid.y + normal.y })} ${pointText(fin.tip)} Q ${pointText({ x: fin.mid.x - normal.x, y: fin.mid.y - normal.y })} ${pointText(fin.root)} Z`;
}

function pedunclePath(points: MarineVec2[], heading: number, model: FishPhysicsRig): string {
  const widths = [7, 5.5, 4, 3];
  const left: MarineVec2[] = [];
  const right: MarineVec2[] = [];
  points.forEach((point, index) => {
    const angle = index ? model.joints[index - 1].angle : heading + Math.PI;
    const normal = { x: -Math.sin(angle) * widths[index], y: Math.cos(angle) * widths[index] };
    left.push({ x: point.x + normal.x, y: point.y + normal.y });
    right.push({ x: point.x - normal.x, y: point.y - normal.y });
  });
  return `M ${pointText(left[0])} L ${left.slice(1).map(pointText).join(" L ")} L ${right.reverse().map(pointText).join(" L ")} Z`;
}

export default function FishPhysicsCharacter({ model, heading, className = "" }: FishPhysicsCharacterProps) {
  const geometry = fishPhysicsGeometry(model, heading);
  const gradientId = `fish-physics-${useId().replace(/:/g, "")}`;
  const base = geometry.points[geometry.points.length - 1];
  const firstTip = geometry.tips[0];
  const lastTip = geometry.tips[geometry.tips.length - 1];
  const curvedTip = (tip: MarineVec2): MarineVec2 => ({
    x: base.x + (tip.x - base.x) * 0.5 + Math.sin(model.joints[2].angle) * 3,
    y: base.y + (tip.y - base.y) * 0.5 - Math.cos(model.joints[2].angle) * 3,
  });
  let membranePath = `M ${pointText(base)} Q ${pointText(curvedTip(firstTip))} ${pointText(firstTip)}`;
  geometry.tips.slice(1).forEach((tip) => { membranePath += ` L ${pointText(tip)}`; });
  membranePath += ` Q ${pointText(curvedTip(lastTip))} ${pointText(base)} Z`;
  const headingDegrees = heading * 180 / Math.PI;
  const orientationDegrees = fishOrientationDegrees(heading, model.facing);
  const whiskerChains = model.whiskerModel?.chains.filter((_, index) => [1, 2, 4, 5].includes(index)) ?? [];

  return (
    <svg className={`fishPhysicsCharacter ${className}`.trim()} viewBox="-60 -45 120 90" role="img" aria-label="Pez" focusable="false">
      <defs>
        <linearGradient id={`${gradientId}-body`} x1="0" y1="-1" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.58" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.16" />
        </linearGradient>
        <linearGradient id={`${gradientId}-tail`} x1="0" y1="-1" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.4" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      <g transform="scale(0.68)">
        <g transform={`rotate(${orientationDegrees})`}>
          <g transform={`scale(${model.facing} 1)`}>
            <g transform={`rotate(${-headingDegrees})`}>
              <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <path d={membranePath} fill={`url(#${gradientId}-tail)`} strokeWidth="1.7" />
                <g opacity="0.52" strokeWidth="1">
                  {geometry.tips.map((tip, index) => <path key={`ray-${index}`} d={quadraticPath(base, curvedTip(tip), tip)} />)}
                </g>
                <path d={pedunclePath(geometry.points, heading, model)} fill={`url(#${gradientId}-tail)`} strokeWidth="1.7" />
              </g>
              <path d={finPath(geometry.fins[0])} fill={`url(#${gradientId}-tail)`} stroke="currentColor" strokeWidth="1.5" opacity="0.72" />
              <g transform={`rotate(${headingDegrees})`} fill={`url(#${gradientId}-body)`} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M -14 -19 Q -11 -36 5 -23 L 13 -20 M -9 17 Q -10 29 7 21" />
                <path d="M -30 -7 C -17 -26 20 -28 36 -3 Q 41 0 36 3 C 20 28 -17 26 -30 7 Z" />
                <path d="M 6 -15 Q -2 0 6 15" fill="none" opacity="0.6" />
                <circle cx="23" cy="-7" r="3" fill="var(--proteo-deep, #07324a)" stroke="none" />
                <path d="M 36 1 L 30 2" fill="none" />
              </g>
              {whiskerChains.length > 0 && (
                <g transform={`translate(31 0) rotate(${headingDegrees - 90}) scale(0.28)`} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.82">
                  {whiskerChains.map((chain, index) => <path key={`whisker-${index}`} d={marineTentaclePath(chain)} />)}
                </g>
              )}
              <path d={finPath(geometry.fins[1])} fill={`url(#${gradientId}-tail)`} stroke="currentColor" strokeWidth="1.7" opacity="0.92" />
              <g fill="none" stroke="currentColor" strokeWidth="1" opacity="0.48">
                {geometry.fins.map((fin, index) => <path key={`fin-vein-${index}`} d={quadraticPath(fin.root, fin.mid, fin.tip)} />)}
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
